import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Tooltip,
  Box,
  Chip,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import * as actions from "../Releases/actions";
import { ListMusic, User, Mail, Music, Calendar, Eye, ExternalLink, Globe, Copy, ClipboardCheck } from 'lucide-react';
import _ from "lodash";
import { toastError, toastSuccess, copyToClipboard } from "../../utils";
import CustomShowModal from "../Common/customShowModal";
import SearchInput from '../../components/SearchInput';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import ActionButton from '../../components/ActionButton';

function CopyMeta({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function Linking(props) {
  const [pListPitchNew, setPListPitchNew] = useState([]);
  const [pListPitchOld, setPListPitchOld] = useState([]);
  const [actionsModal, setActionsModal] = useState(false);
  const [detailsData, setDetailsData] = useState();
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [actionId, setActionId] = useState();
  const [actionType, setActionType] = useState('');
  const [count, setCount] = useState(0);

  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
    search: "",
  });

  const delaySearch = useCallback(
    _.debounce((filters) => onGetPListPitchOld(filters), 500),
    []
  );

  useEffect(() => {
    document.title = 'PP | Playlist Pitching';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    onGetPListPitchOld(filters);
    setTimeout(() => {
      props.setLoader(false);
    }, 5000);
  }, []);

  const onGetPListPitchOld = (filters) => {
    props.setLoader(true);
    actions
      .getPitchOldAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setPListPitchOld(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetPListPitchOld(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onGetPListPitchNew = () => {
    props.setLoader(true);
    actions
      .getPitchRequestAdmin()
      .then((res) => {
        setPListPitchNew(res.body);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load pitch requests');
      });
  };

  useEffect(() => {
    onGetPListPitchNew();
  }, []);

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const handleViewDetails = (data) => {
    setDetailsData(data);
    setViewDetailsModal(true);
  };

  const handleActionOnClick = (item, type) => {
    setActionId(item);
    setActionType(type);
    setActionsModal(true);
  };

  const onConfirmAcceptModal = (status) => {
    props.setLoader(true);
    actions
      .updatePitchStatusAdmin({ status: status, pitch_id: actionId })
      .then((res) => {
        setActionsModal(false);
        onGetPListPitchNew();
        onGetPListPitchOld(filters);
        toastSuccess(res.message || 'Pitch status updated successfully');
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update pitch status');
      });
  };

  const renderCard = (item, isNew) => (
    <Paper
      key={item.pitch_id}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <ListMusic size={20} color="#64748B" />
          <span>Pitch</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.release_title || 'Untitled'}
            </Typography>
          </div>
          <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <ActionButton variant="info" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }} onClick={() => handleViewDetails(item)}>
              View Details
            </ActionButton>
            <Box sx={{ minWidth: 80, display: 'flex', justifyContent: 'center' }}>
              {isNew ? (
                <Box display="flex" gap={0.75}>
                  <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => handleActionOnClick(item.pitch_id, 'approved')}>
                    Accept
                  </ActionButton>
                  <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => handleActionOnClick(item.pitch_id, 'rejected')}>
                    Reject
                  </ActionButton>
                </Box>
              ) : (
                <StatusBadge status={item.status} />
              )}
            </Box>
          </div>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-PT-{item.pitch_id}</span>
          <span className="approved-release-card__id">RL-{item.release_id}</span>
          <CopyMeta icon={<Music size={13} />} label="Audio" value={item.audio_title} />
        </div>
        <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
          <CopyMeta icon={<User size={13} />} label="User" value={item.name} />
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          {item.date && (
            <CopyMeta icon={<Calendar size={13} />} label="Date" value={item.date} />
          )}
        </div>
      </div>
    </Paper>
  );

  return (
    <Grid container>
      {/* ========== Review Required ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Review Required
          </Typography>
          <Chip
            icon={<ListMusic size={14} />}
            label={`${pListPitchNew.length} Pending`}
            sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              color: '#F59E0B',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#F59E0B' },
            }}
          />
        </Box>
        <Box className="approved-release-list" mb={4}>
          {pListPitchNew.length ? (
            pListPitchNew.map((item) => renderCard(item, true))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Pitches To Review" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ borderColor: 'var(--border)', mb: 3 }} />
      </Grid>

      {/* ========== History ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Playlist Pitching History
          </Typography>
          <Chip
            icon={<ListMusic size={14} />}
            label={`${count} Total`}
            sx={{
              backgroundColor: 'rgba(108, 99, 255, 0.12)',
              color: '#6C63FF',
              border: '1px solid rgba(108, 99, 255, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#6C63FF' },
            }}
          />
        </Box>
        <Box mb={3} sx={{ maxWidth: 420 }}>
          <SearchInput
            onChange={onChangeSearch}
            value={filters.search}
            placeholder="Search by name, email, or release..."
          />
        </Box>
        <Box className="approved-release-list">
          {pListPitchOld.length ? (
            pListPitchOld.map((item) => renderCard(item, false))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Pitching History Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#94A3B8',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(108, 99, 255, 0.2)',
                    color: '#6C63FF',
                  },
                },
              }}
            />
          </Box>
        )}
      </Grid>

      {/* ========== Action Modal ========== */}
      <CustomShowModal
        handleClose={() => setActionsModal(false)}
        title={actionType === 'approved' ? 'Accept Pitch Request' : 'Reject Pitch Request'}
        open={actionsModal}
        maxWidth="sm"
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
          <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem' }}>
            Are you sure you want to {actionType === 'approved' ? 'accept' : 'reject'} this pitch request?
          </Typography>
          <Box display="flex" gap={2}>
            <ActionButton variant={actionType === 'approved' ? 'success' : 'danger'} onClick={() => onConfirmAcceptModal(actionType)}>
              {actionType === 'approved' ? 'Accept' : 'Reject'}
            </ActionButton>
            <ActionButton variant="warning" onClick={() => setActionsModal(false)}>
              Cancel
            </ActionButton>
          </Box>
        </Box>
      </CustomShowModal>

      {/* ========== Details Modal ========== */}
      <CustomShowModal
        open={viewDetailsModal}
        handleClose={() => setViewDetailsModal(false)}
        maxWidth="sm"
        title={detailsData?.release_title || "Pitch Details"}
      >
        <Box py={1}>
          {detailsData?.artwork_asset && (
            <CopyableLink label="Artwork Assets" value={detailsData.artwork_asset} />
          )}
          {detailsData?.additional_assets && (
            <CopyableLink label="Additional Assets" value={detailsData.additional_assets} />
          )}
          {detailsData?.moods?.length > 0 && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                Moods
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {detailsData.moods.map((mood, idx) => (
                  <Chip key={idx} label={mood} size="small" sx={{ backgroundColor: 'rgba(108, 99, 255, 0.12)', color: '#6C63FF', fontSize: '0.75rem' }} />
                ))}
              </Box>
            </Box>
          )}
          {detailsData?.press_release?.length > 0 && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                Press Releases
              </Typography>
              {detailsData.press_release.map((item, idx) => (
                <Typography key={idx} sx={{ color: '#F1F5F9', fontSize: '0.85rem', wordBreak: 'break-word' }}>
                  {item}
                </Typography>
              ))}
            </Box>
          )}
          {detailsData?.social_media?.length > 0 && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                Social Media Links
              </Typography>
              {detailsData.social_media.map((link, idx) => (
                <CopyableLink key={idx} label="" value={link} icon={<Globe size={13} color="#64748B" />} compact />
              ))}
            </Box>
          )}
          {detailsData?.why_pitch && (
            <CopyableText label="User Pitch" value={detailsData.why_pitch} />
          )}
          {detailsData?.quick_pitch && (
            <CopyableText label="Quick Pitch" value={detailsData.quick_pitch} />
          )}
          {detailsData?.additional_info && (
            <CopyableText label="Additional Information" value={detailsData.additional_info} />
          )}
        </Box>
      </CustomShowModal>
    </Grid>
  );
}

function CopyableLink({ label, value, icon, compact }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box className="copyable-field" sx={{ py: compact ? 0.5 : 1 }}>
      {label && <span className="copyable-field__label">{label}</span>}
      {icon && icon}
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'var(--primary)', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
      >
        {value}
      </a>
      <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
        <IconButton
          size="small"
          onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          sx={{ color: copied ? '#10B981' : 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' }, ml: 0.5 }}
        >
          {copied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Open" arrow placement="top">
        <IconButton size="small" component="a" href={value} target="_blank" rel="noreferrer" sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
          <ExternalLink size={14} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function CopyableText({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box className="copyable-field" sx={{ py: 1 }}>
      <span className="copyable-field__label">{label}</span>
      <Typography sx={{ color: '#F1F5F9', fontSize: '0.85rem', lineHeight: 1.6, wordBreak: 'break-word', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Typography>
      <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
        <IconButton
          size="small"
          onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          sx={{ color: copied ? '#10B981' : 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' }, ml: 0.5 }}
        >
          {copied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
